// autocompletePlugin.js
// Simple autocomplete for @ mentions and # hashtags using prosemirror-autocomplete

import autocomplete from 'prosemirror-autocomplete';
import {searchUsersForMention} from '../helpers/mentions/searchUsers';
import {createTagForMention, searchTagsForMention} from '../helpers/mentions/searchTags';
import {getAwpBackendOrigin} from '../../../config/apiBase';
import {searchWikidataEntities, buildWikidataEntityMarkAttrs} from '../helpers/wikidataHelpers';

/** Fetch a /api/users/{id}/avatar blob with the stored JWT and set it as img.src. */
function loadAvatarWithAuth(imgEl, relativeUrl) {
    const origin = getAwpBackendOrigin();
    const url = relativeUrl.startsWith('http') ? relativeUrl : `${origin}${relativeUrl}`;
    const token = localStorage.getItem('access_token');
    fetch(url, {headers: token ? {Authorization: `Bearer ${token}`} : {}})
        .then((res) => (res.ok ? res.blob() : null))
        .then((blob) => {
            if (blob && imgEl.isConnected) {
                imgEl.src = URL.createObjectURL(blob);
            }
        })
        .catch(() => {/* leave initials placeholder */});
}

/**
 * Create autocomplete plugins for mentions and hashtags
 * Returns array of 2 plugins (decoration + inputRule) from prosemirror-autocomplete
 *
 * @param {Object} options - Configuration options
 * @param {string} options.docId - Document ID for hashtag context
 * @param {Object} options.hashtagOptions - Additional hashtag options (segmentId, useAISuggestions)
 * @returns {Array<Plugin>} Array of 2 ProseMirror plugins
 */
export function createAutocompletePlugins(options = {}) {
    const {docId, hashtagOptions = {}} = options;

    // State maintained in closure (shared across handlers)
    let dropdown = null;
    let items = [];
    let selectedIndex = 0;
    let currentView = null;
    let currentRange = null;
    let currentType = null;
    let clickOutsideHandler = null;
    // AbortController for the in-flight search request. Aborted and replaced on
    // every new search so stale results from slow requests never render after close.
    let searchController = null;
    const recentSelectionsByType = {
        mention: [],
        hashtag: [],
        wikidata: [],
    };

    function rememberRecentSelection(typeName, item) {
        const key = (item?.id || item?.name || item?.label || "").toString();
        if (!typeName || !key) return;

        const previous = recentSelectionsByType[typeName] || [];
        recentSelectionsByType[typeName] = [key, ...previous.filter((v) => v !== key)].slice(0, 5);
    }

    function normalizeAndRankItems(typeName, rawItems, filter) {
        const query = (filter || "").trim().toLowerCase();
        const seen = new Set();
        const unique = [];

        (Array.isArray(rawItems) ? rawItems : []).forEach((item) => {
            const key = (item?.id || item?.name || item?.label || "").toString().toLowerCase();
            if (!key || seen.has(key)) return;
            seen.add(key);
            unique.push(item);
        });

        if (!query) {
            const recent = recentSelectionsByType[typeName] || [];
            return unique.sort((a, b) => {
                const aKey = (a.id || a.name || a.label || "").toString();
                const bKey = (b.id || b.name || b.label || "").toString();
                const ai = recent.indexOf(aKey);
                const bi = recent.indexOf(bKey);
                const as = ai === -1 ? Number.POSITIVE_INFINITY : ai;
                const bs = bi === -1 ? Number.POSITIVE_INFINITY : bi;
                return as - bs;
            });
        }

        const rankMention = (item) => {
            const label = (item.label || item.name || "").toLowerCase();
            const email = (item.email || "").toLowerCase();
            if (label.startsWith(query)) return 0;
            if (email.startsWith(query)) return 1;
            if (label.includes(query)) return 2;
            if (email.includes(query)) return 3;
            return 4;
        };

        const rankTag = (item) => {
            if (item.__create) return 10;
            const label = (item.label || item.name || "").toLowerCase();
            if (label.startsWith(query)) return 0;
            if (label.includes(query)) return 1;
            return 2;
        };

        const rankWikidata = (item) => {
            const label = (item.label || "").toLowerCase();
            if (label.startsWith(query)) return 0;
            if (label.includes(query)) return 1;
            return 2;
        };

        const rank = typeName === "mention" ? rankMention
            : typeName === "wikidata" ? rankWikidata
            : rankTag;
        return unique.sort((a, b) => {
            const diff = rank(a) - rank(b);
            if (diff !== 0) return diff;
            const an = (a.label || a.name || "").toLowerCase();
            const bn = (b.label || b.name || "").toLowerCase();
            return an.localeCompare(bn);
        });
    }

    /**
     * Cleanup dropdown and reset state
     */
    function cleanupDropdown() {
        if (dropdown && dropdown.parentNode) {
            dropdown.parentNode.removeChild(dropdown);
        }
        if (clickOutsideHandler) {
            document.removeEventListener('mousedown', clickOutsideHandler);
            clickOutsideHandler = null;
        }
        // Cancel any in-flight search so stale results don't re-open the dropdown.
        if (searchController) {
            searchController.abort();
            searchController = null;
        }
        dropdown = null;
        items = [];
        selectedIndex = 0;
        currentView = null;
        currentRange = null;
        currentType = null;
    }

    /**
     * Handle item selection (used by click handlers)
     */
    function selectItem(index) {
        if (!currentView || !currentRange || !currentType) {
            console.error('[autocomplete] selectItem called without current state');
            return false;
        }

        if (items.length === 0 || index >= items.length) return false;

        let item = items[index];

        // Handle tag creation asynchronously.
        // Snapshot view and range NOW — they will be stale by the time the
        // async .then() fires if the user has continued typing.
        if (currentType.name === 'hashtag' && item.__create) {
            const snapshotView = currentView;
            const snapshotRange = {...currentRange};
            const snapshotType = currentType;
            createTagForMention(item.name, docId)
                .then(createdTag => {
                    rememberRecentSelection(snapshotType.name, createdTag);
                    insertMention(snapshotView, snapshotRange, snapshotType.name, createdTag);
                    cleanupDropdown();
                })
                .catch(err => {
                    console.error('[autocomplete] Tag creation error:', err);
                    cleanupDropdown();
                });
            return true;
        }

        insertMention(currentView, currentRange, currentType.name, item);
        rememberRecentSelection(currentType.name, item);
        cleanupDropdown();
        return true;
    }

    /**
     * Create and position standard dropdown element
     */
    function createDropdown(coords) {
        const container = document.createElement('div');
        container.className = 'prosemirror-suggestion';
        container.setAttribute('role', 'listbox');
        container.setAttribute('aria-label', 'Autocomplete suggestions');

        // Standard positioning with absolute positioning
        container.style.cssText = `
            position: absolute;
            top: ${coords.bottom}px;
            left: ${coords.left}px;
            z-index: 1000;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            max-height: 300px;
            overflow-y: auto;
            min-width: 200px;
        `;

        document.body.appendChild(container);

        // Close on click outside
        const handleClickOutside = (e) => {
            if (container && !container.contains(e.target)) {
                cleanupDropdown();
                document.removeEventListener('mousedown', handleClickOutside);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return container;
    }

    /**
     * Update dropdown position
     */
    function positionDropdown(coords) {
        if (!dropdown) return;
        dropdown.style.top = `${coords.bottom}px`;
        dropdown.style.left = `${coords.left}px`;
    }

    /**
     * Render dropdown with items
     */
    function renderDropdown(container, items, selectedIndex) {
        if (!container) {
            console.warn('[autocomplete] renderDropdown called with null container');
            return;
        }

        container.innerHTML = '';

        if (items.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'prosemirror-suggestion-empty';
            empty.style.cssText = 'padding: 12px; color: #999; text-align: center;';
            empty.textContent = 'No results';
            container.appendChild(empty);
            return;
        }

        const ul = document.createElement('ul');
        ul.className = 'prosemirror-suggestion-list';
        ul.setAttribute('role', 'presentation');
        ul.style.cssText = 'list-style: none; margin: 0; padding: 0;';

        items.forEach((item, i) => {
            const li = document.createElement('li');
            li.className = 'prosemirror-suggestion-item' + (i === selectedIndex ? ' is-selected' : '');
            li.setAttribute('data-index', String(i));
            li.setAttribute('role', 'option');
            li.setAttribute('aria-selected', i === selectedIndex ? 'true' : 'false');

            // Base styles
            li.style.cssText = `
                padding: 8px 12px;
                cursor: pointer;
                display: flex;
                align-items: center;
                gap: 8px;
                transition: background-color 0.15s;
                background-color: ${i === selectedIndex ? '#f0f0f0' : 'white'};
            `;

            // Format based on type (user vs tag)
            const formatted = formatItem(item);

            // Avatar: always show a circle — image if available, initials fallback.
            {
                const avatarWrap = document.createElement('div');
                avatarWrap.style.cssText = 'width:32px;height:32px;border-radius:50%;flex-shrink:0;overflow:hidden;background:#1976d2;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:600;color:#fff;';

                if (formatted.avatar) {
                    const img = document.createElement('img');
                    img.alt = formatted.title || '';
                    img.width = 32;
                    img.height = 32;
                    img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:50%;';
                    img.onerror = () => {
                        img.remove();
                        avatarWrap.textContent = (formatted.title || '?')[0].toUpperCase();
                    };

                    if (formatted.avatar.includes('/api/users/') && formatted.avatar.includes('/avatar')) {
                        // Needs JWT — fetch with auth header then use a blob URL.
                        loadAvatarWithAuth(img, formatted.avatar);
                    } else {
                        img.src = formatted.avatar;
                    }
                    avatarWrap.appendChild(img);
                } else {
                    avatarWrap.textContent = (formatted.title || '?')[0].toUpperCase();
                }

                li.appendChild(avatarWrap);
            }

            const textContainer = document.createElement('div');
            textContainer.className = 'prosemirror-suggestion-text';
            textContainer.style.cssText = 'flex: 1; min-width: 0;';

            const title = document.createElement('div');
            title.className = 'prosemirror-suggestion-title';
            title.style.cssText = 'font-weight: 500; color: #333; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
            title.textContent = formatted.title || '';
            textContainer.appendChild(title);

            if (formatted.subtitle) {
                const subtitle = document.createElement('div');
                subtitle.className = 'prosemirror-suggestion-subtitle';
                subtitle.style.cssText = 'font-size: 0.875rem; color: #666; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
                subtitle.textContent = formatted.subtitle;
                textContainer.appendChild(subtitle);
            }

            li.appendChild(textContainer);

            // Mouse interaction
            li.onmouseenter = () => {
                selectedIndex = i;
                Array.from(ul.children).forEach((child, idx) => {
                    child.style.backgroundColor = idx === i ? '#f0f0f0' : 'white';
                    child.classList.toggle('is-selected', idx === i);
                    child.setAttribute('aria-selected', idx === i ? 'true' : 'false');
                });
            };

            li.onmouseleave = () => {
                if (i !== selectedIndex) {
                    li.style.backgroundColor = 'white';
                }
            };

            // Click to select
            li.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                selectItem(i);
            };

            ul.appendChild(li);
        });

        container.appendChild(ul);

        const hint = document.createElement('div');
        hint.className = 'prosemirror-suggestion-hint';
        hint.style.cssText = 'padding: 8px 12px; font-size: 11px; color: #6b7280; border-top: 1px solid #e5e7eb;';
        hint.textContent = 'Up/Down to navigate, Enter to select, Esc to close';
        container.appendChild(hint);
    }

    /**
     * Format item for display (user vs tag)
     */
    function formatItem(item) {
        if (item.email) {
            // User mention
            return {
                avatar: item.headshot,
                title: item.label || item.name,
                subtitle: item.position || item.email,
            };
        } else if (item.id && /^Q\d+$/i.test(String(item.id))) {
            // Wikidata entity
            return {
                avatar: item.imageUrl || null,
                title: item.label || item.id,
                subtitle: item.description || item.url || null,
            };
        } else {
            // Hashtag
            return {
                title: item.label || item.name,
                subtitle: item.__create ? 'Create new tag' : null,
            };
        }
    }

    /**
     * Insert mention inline node into document
     * Following prosemirror-autocomplete pattern: deleteRange then insert
     */
    function insertMention(view, range, type, item) {
        const {state} = view;
        const schema = state.schema;

        if (type === 'mention' && !schema.nodes.userMention) {
            console.error('[insertMention] userMention node not found in schema');
            return false;
        }

        if (type === 'hashtag' && !schema.nodes.hashtag) {
            console.error('[insertMention] hashtag node not found in schema');
            return false;
        }

        try {
            const {from, to} = range;

            if (type === 'mention') {
                // User mention - create inline node
                const mentionNode = schema.nodes.userMention.create({
                    id: item.id,
                    label: item.label || item.name,
                    name: item.name,
                    email: item.email,
                    position: item.position,
                    headshot: item.headshot,
                });

                // Delete the autocomplete range and insert mention node + space
                const tr = state.tr
                    .deleteRange(from, to)
                    .insert(from, mentionNode)
                    .insertText("\u00A0");

                view.dispatch(tr);
                return true;

            } else if (type === 'hashtag') {                // Hashtag - create inline node
                const hashtagNode = schema.nodes.hashtag.create({
                    id: item.id,
                    label: item.label || item.name,
                    docId: docId,
                });

                // Delete the autocomplete range and insert hashtag node + space
                const tr = state.tr
                    .deleteRange(from, to)
                    .insert(from, hashtagNode)
                    .insertText("\u00A0");

                view.dispatch(tr);
                return true;

            } else if (type === 'wikidata') {
                // Wikidata entity — insert label text with the wikidataEntity mark applied
                const markType = schema.marks.wikidataEntity;
                if (!markType) {
                    console.error('[insertMention] wikidataEntity mark not found in schema');
                    return false;
                }

                const label = item.label || item.id || '';
                const mark = markType.create(buildWikidataEntityMarkAttrs(item));

                const tr = state.tr
                    .deleteRange(from, to)
                    .insertText(label, from)
                    .addMark(from, from + label.length, mark)
                    .insertText('\u00A0');

                view.dispatch(tr);
                return true;

            } else {
                console.error('[insertMention] Unknown type:', type);
                return false;
            }
        } catch (err) {
            console.error('[insertMention] Error:', err);
            return false;
        }
    }

    /**
     * Perform search based on trigger type.
     * @param {string} triggerName
     * @param {string} filter
     * @param {AbortSignal} signal - Abort signal; callers should check signal.aborted after await.
     */
    async function performSearch(triggerName, filter, signal) {
        if (triggerName === 'mention') {
            return await searchUsersForMention(filter, {signal});
        } else if (triggerName === 'wikidata') {
            if (!filter || filter.trim().length < 2) return [];
            return await searchWikidataEntities(filter.trim(), {signal});
        } else {
            // Hashtag search - use AI suggestions if configured
            const {segmentId, useAISuggestions} = hashtagOptions;

            if (useAISuggestions && segmentId) {
                // Use AI-powered search
                const {searchTagsWithSuggestions} = await import('../helpers/mentions/searchTags');
                return await searchTagsWithSuggestions(filter, docId, {
                    segmentId,
                    includeSuggestions: true,
                    signal,
                });
            }

            return await searchTagsForMention(filter, docId, {signal});
        }
    }

    // Configure triggers
    const triggers = [
        {
            name: 'mention',
            trigger: '@',
            cancelOnFirstSpace: false,  // Allow spaces in names
        },
        {
            name: 'hashtag',
            trigger: '#',
            cancelOnFirstSpace: false,  // Allow spaces in tags
        },
        {
            name: 'wikidata',
            trigger: '[[',
            cancelOnFirstSpace: false,  // Allow spaces in entity names
        },
    ];

    // Return array of plugins from autocomplete
    return autocomplete({
        triggers,

        onOpen: async ({view, range, trigger, filter, type}) => {
            // Store current state for click handlers
            currentView = view;
            currentRange = range;
            currentType = type;

            const coords = view.coordsAtPos(range.from);
            dropdown = createDropdown(coords);

            // Cancel any previous in-flight search and start a fresh one.
            if (searchController) searchController.abort();
            searchController = new AbortController();
            const {signal} = searchController;

            try {
                const results = await performSearch(type.name, filter, signal);
                // Discard if this search was superseded or the dropdown was closed.
                if (signal.aborted || !dropdown) return true;
                items = normalizeAndRankItems(type.name, results, filter);
                selectedIndex = 0;
                renderDropdown(dropdown, items, selectedIndex);
            } catch (err) {
                if (err.name === 'AbortError') return true;
                console.error('[autocomplete] Search error:', err);
                items = [];
                if (dropdown) renderDropdown(dropdown, items, selectedIndex);
            }

            return true;
        },

        onFilter: async ({view, range, filter, type}) => {
            // Update current state
            currentView = view;
            currentRange = range;
            currentType = type;

            // Cancel previous search and start a fresh one.
            if (searchController) searchController.abort();
            searchController = new AbortController();
            const {signal} = searchController;

            try {
                const results = await performSearch(type.name, filter, signal);
                if (signal.aborted || !dropdown) return true;
                items = normalizeAndRankItems(type.name, results, filter);
                selectedIndex = 0;
                renderDropdown(dropdown, items, selectedIndex);
            } catch (err) {
                if (err.name === 'AbortError') return true;
                console.error('[autocomplete] Search error:', err);
                items = [];
                if (dropdown) renderDropdown(dropdown, items, selectedIndex);
            }

            // Update position
            const coords = view.coordsAtPos(range.from);
            positionDropdown(coords);

            return true;
        },

        onArrow: ({kind}) => {
            if (!items.length) return false;

            if (kind === 'ArrowDown') {
                selectedIndex = (selectedIndex + 1) % items.length;
            } else if (kind === 'ArrowUp') {
                selectedIndex = (selectedIndex - 1 + items.length) % items.length;
            } else {
                return false;
            }

            if (dropdown) {
                renderDropdown(dropdown, items, selectedIndex);

                // Scroll selected item into view
                const selectedItem = dropdown.querySelector('.is-selected');
                if (selectedItem) {
                    selectedItem.scrollIntoView({block: 'nearest'});
                }
            }

            return true;
        },

        onEnter: ({view, range, type}) => {
            // If no dropdown or no items, let default behavior handle Enter
            if (!dropdown || items.length === 0) {
                cleanupDropdown();
                return false;
            }

            // Update current state
            currentView = view;
            currentRange = range;
            currentType = type;

            let item = items[selectedIndex];

            // Handle tag creation asynchronously but return synchronously.
            // Snapshot view and range before the await so stale closure values
            // aren't used after the network round-trip.
            if (type.name === 'hashtag' && item.__create) {
                const snapshotView = view;
                const snapshotRange = {...range};
                createTagForMention(item.name, docId)
                    .then(createdTag => {
                        rememberRecentSelection(type.name, createdTag);
                        insertMention(snapshotView, snapshotRange, type.name, createdTag);
                        cleanupDropdown();
                    })
                    .catch(err => {
                        console.error('[autocomplete] Tag creation error:', err);
                        cleanupDropdown();
                    });
                return true; // Return true to close autocomplete decorations
            }

            // Insert mention synchronously
            insertMention(view, range, type.name, item);
            rememberRecentSelection(type.name, item);
            cleanupDropdown();
            return true;
        },

        onClose: () => {
            cleanupDropdown();
            return true;
        },
    });
}

